import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  ContentChild,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
  output,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import {
  FormControl,
  FormControlDirective,
  FormControlName,
  FormControlStatus,
  NgModel,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldModule,
  SubscriptSizing,
} from '@angular/material/form-field';
import { MatSelect, MatSelectChange, MatSelectModule } from '@angular/material/select';
import { ErrorStateMatcher } from '@angular/material/core';

import { MatSelectSearchComponent, NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { NoopValueAccessorDirective } from '@rp/shared/directives';
import { FieldErrorStateMatcher, injectNgControl } from '@rp/utils';
import { Validation } from '@rp/shared/validators';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, filter, startWith } from 'rxjs';
import { FILTER_DELAY } from '@rp/shared/models';

import { IconComponent, IconName } from '../icon';
import { SpinnerComponent } from '../spinner';

@Component({
  selector: 'rp-select',
  standalone: true,
  templateUrl: './select.component.html',
  styleUrl: './select.component.scss',
  hostDirectives: [NoopValueAccessorDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
    IconComponent,
    TranslateModule,
    SpinnerComponent,
    NgTemplateOutlet,
  ],
  providers: [
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { floatLabel: 'always' } },
    { provide: ErrorStateMatcher, useClass: FieldErrorStateMatcher },
  ],
})
export class SelectComponent<T> implements OnInit {
  @ContentChild('itemTpl') itemTpl: TemplateRef<unknown>;

  select = viewChild(MatSelect);
  selectSearch = viewChild(MatSelectSearchComponent);

  label = input<string>();
  displayField = input<keyof T>();
  hiddenField = input<keyof T>('isHidden' as keyof T);
  selectedField = input<keyof T>();
  items = input.required<T[] | null>();
  icons = input<IconName[]>([]);
  placeholder = input<string>('');
  subscriptSizing = input<SubscriptSizing>('fixed');
  compareObjectWithField = input<keyof T>('id' as keyof T);
  isDisabled = input(false, {
    transform: booleanAttribute,
  });
  multiple = input(false, {
    transform: booleanAttribute,
  });
  searchable = input(false, {
    transform: booleanAttribute,
  });
  searchBy = input<(keyof T)[]>([]);
  showErrorMessage = input(true, {
    transform: booleanAttribute,
  });
  translateOptions = input(false, {
    transform: booleanAttribute,
  });
  loading = input(false, {
    transform: booleanAttribute,
  });
  showCountryIcons = input(false, {
    transform: booleanAttribute,
  });
  showCustomLabel = input(false, {
    transform: booleanAttribute,
  });
  matcher = input<FieldErrorStateMatcher>();
  clearable = input(false, {
    transform: booleanAttribute,
  });

  requestable = input(false, {
    transform: booleanAttribute,
  });

  selectionChange = output<MatSelectChange>();
  selectScroll = output();
  searchChanged = output<string>();
  selectSearchChanged = output<Event>();

  firstError = signal<string>('');
  firstErrorKey = signal<ValidationErrors>({});
  errors = signal<Validation[]>([]);

  requestedSelectedItem = signal<T | T[]>(null);

  combinedItems = computed<T[]>(() => {
    const items = this.items() ? [...this.items()] : [];
    const requested = this.requestedSelectedItem();
    const selectedField = this.selectedField();

    if (requested == null) {
      return items;
    }

    const isMultiple = this.multiple();
    const isArray = isMultiple && Array.isArray(requested);

    const isItemInList = (item: T) => items.some(i => i[selectedField] === item[selectedField]);

    if (isArray) {
      (requested as T[]).forEach(selectedItem => {
        if (!isItemInList(selectedItem)) {
          items.unshift(selectedItem);
        }
      });
    } else {
      if (!isItemInList(requested as T)) {
        items.unshift(requested as T);
      }
    }

    return items;
  });

  panelClass = computed<string>(() => {
    let panelClass = 'rp-select-panel';

    if (this.searchable()) {
      panelClass = `${panelClass} with-search`;
    }

    if (this.multiple()) {
      panelClass = `${panelClass} with-multiple`;
    }

    const areAllItemsHidden =
      !this.searchable() && this.combinedItems()?.every(item => item[this.hiddenField()]);

    if (areAllItemsHidden) {
      panelClass = `${panelClass} with-empty-list`;
    }

    return panelClass;
  });

  filteredItems = computed<T[]>(() => {
    let search = this._value().trim();

    if (!this.loading() && this.combinedItems()) {
      if (!search) {
        return [...this.combinedItems()];
      } else {
        search = search.toLowerCase();

        return this.combinedItems().filter(item => {
          const filters = this.searchBy();
          const searchValue = search.toLowerCase();

          if (filters.length) {
            return filters.some(filter => {
              const fieldValue = item[filter];

              return fieldValue && fieldValue.toString().toLowerCase().includes(searchValue);
            });
          }

          const displayField = this.displayField();
          if (displayField && item[displayField]) {
            return item[displayField].toString().toLowerCase().includes(searchValue);
          }

          return item.toString().toLowerCase().includes(searchValue);
        });
      }
    } else if (this.requestable() && this.loading() && this.combinedItems()) {
      return [...this.combinedItems()];
    } else {
      return [];
    }
  });

  ngControl: FormControlDirective | FormControlName | NgModel = injectNgControl();
  filterControl: FormControl<string> = new FormControl<string>('');
  isPrimitiveValue = false;
  iconNames = IconName;

  private originalOptionsLengthOffsetFn: () => number;
  private scrollPanel: HTMLElement | null = null;
  private lastScrollHeight = 0;

  private _value = signal<string>('');
  private _destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      const { control } = this.ngControl;

      if (this.isDisabled()) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    this._listenControlChangesWhenRequesting();
    this._listenFilterControlChanges();

    this._listenControlStatusChanges();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSelectSearch(_: Event): void {
    // for geo select to override
  }

  onClosed(): void {
    this.filterControl.setValue('');

    if (this.selectSearch() && this.originalOptionsLengthOffsetFn) {
      this.selectSearch()['getOptionsLengthOffset'] = this.originalOptionsLengthOffsetFn;
    }
  }

  attachScrollEvent(): void {
    this.scrollPanel = document.querySelector('.rp-select-panel');
    if (this.scrollPanel) {
      this.scrollPanel.addEventListener('scroll', this.onLazyScrollHandler.bind(this));
    }
  }

  detachScrollEvent(): void {
    if (this.scrollPanel) {
      this.scrollPanel.removeEventListener('scroll', this.onLazyScrollHandler.bind(this));
      this.scrollPanel = null;
    }
  }

  onLazyScrollHandler(event: Event): void {
    const target = event.target as HTMLElement;
    const bottomReached = target.scrollTop + target.clientHeight >= target.scrollHeight - 1;

    if (bottomReached && !this.loading() && target.scrollHeight > this.lastScrollHeight) {
      this.selectScroll.emit();
      this.lastScrollHeight = target.scrollHeight;
    }
  }

  onOpened(): void {
    if (this.selectSearch()) {
      this.originalOptionsLengthOffsetFn = this.selectSearch()['getOptionsLengthOffset'].bind(
        this.selectSearch(),
      );

      this.selectSearch()['getOptionsLengthOffset'] = () => {
        const control = this.ngControl.control;

        if (this.multiple() && control.value?.length) {
          return this.originalOptionsLengthOffsetFn() + control.value.length;
        } else {
          return this.originalOptionsLengthOffsetFn();
        }
      };
    }
  }

  getSelectedItem(values: T | T[]): T | T[] {
    const selectedField = this.selectedField();

    const isMatch = (item: T, value: T) =>
      selectedField ? item[selectedField] === value : item === value;

    if (Array.isArray(values)) {
      return values
        .map(value => this.combinedItems()?.find(item => isMatch(item, value)))
        .filter(Boolean) as T[];
    }

    return this.combinedItems()?.find(item => isMatch(item, values));
  }

  onClear(ev: MouseEvent): void {
    ev.stopPropagation();

    this.ngControl.control.reset();
    this.ngControl.control.markAsDirty();
    this.selectionChange.emit(null);
  }

  compareWithFn = (option: T, value: T): boolean => {
    const compareField = this.compareObjectWithField();

    if (option === undefined || option === null || value === null) {
      return false;
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      return option[compareField] === value[compareField];
    }

    const optionValue = typeof option === 'object' ? option[compareField] : option;

    return optionValue === value;
  };

  updateOverlayPosition(): void {
    const select = this.select();

    if (select.panelOpen) {
      const overlayDir = select['_overlayDir'];
      const overlayRef = overlayDir.overlayRef;

      requestAnimationFrame(() => {
        overlayRef.updatePosition();
      });
    }
  }

  private _listenControlChangesWhenRequesting(): void {
    this.ngControl.control.valueChanges
      .pipe(
        filter(() => this.requestable() && !this.isPrimitiveValue),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((value: T) => this.requestedSelectedItem.set(this.getSelectedItem(value) as T));
  }

  private _listenFilterControlChanges(): void {
    this.filterControl.valueChanges
      .pipe(
        debounceTime(this.requestable() ? FILTER_DELAY : 0),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((value: string) => {
        if (this.requestable()) {
          this.searchChanged.emit(value);
        } else {
          this._value.set(value);
        }
      });
  }

  private _listenControlStatusChanges(): void {
    this.ngControl.control.statusChanges
      .pipe(
        startWith(this.ngControl.control.status),
        filter((status: FormControlStatus) => status === 'INVALID'),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(() => {
        const { control } = this.ngControl;

        this.errors.set(Object.keys(control.errors ?? {}) as Validation[]);
        this.firstError.set(this.errors().length ? 'validations.' + this.errors()[0] : '');
        this.firstErrorKey.set(control.errors ?? {});
      });
  }
}
